import React from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    VIEW,
    LOW_DEMAND_MODE,
    HIGH_DEMAND_MODE,
    CONTINUOUS_MODE,
    NO_DANGEROUS_FAILURE,
    NO_SAFE_FAILURE,
    NO_ARCHITECTURAL_CONSTRAINTS,
    IEC_61508_2010,
    IEC_61508_1998,
    IEC_61511_2016,
    IEC_61511_2003,
    VDI_VDE_2180_2019,
    // ISA_TR84_2002,
    ISA_TR84_2022,
} from "const"
import {
    PRM_ACCOUNT_SETTINGS,
    StoreType,
    ChangeUserSettings,
    updateUserSettings,
} from "client/store"
import { hasAccess } from "client/utils"
import {
    FormGroup,
    Label,
    Input,
    CheckboxWrapper,
    RadioButton,
} from "client/components"

import DefaultAccountSettings from "../defaultAccountSettings"

const MainSettings = () => {
    const settings = useSelector((state: StoreType) => state.userSettings.settings.sil)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const onChange = (value: ChangeUserSettings) => {
        dispatch(updateUserSettings("sil", value))
    }

    const {
        defaultSafetyFunctionName,
        defaultMode,
        defaultCalculationMethodDangerous,
        defaultCalculationMethodSafe,
        defaultArchitecturalConstraints,
    } = settings

    const viewAccess = hasAccess(PRM_ACCOUNT_SETTINGS, VIEW)

    return (
        <Row>
            <Col xs={12}>
                <FormGroup>
                    <Label>{lang.defaultSafetyFunctionName}</Label>
                    <Input
                        type="text"
                        value={defaultSafetyFunctionName}
                        onChange={(e) => onChange({ defaultSafetyFunctionName: e.target.value })}
                    />
                </FormGroup>

                <FormGroup>
                    <Label>{lang.defaultMode}</Label>
                    <CheckboxWrapper displayColumn>
                        <RadioButton
                            checked={defaultMode === LOW_DEMAND_MODE}
                            onChange={() => onChange({ defaultMode: LOW_DEMAND_MODE })}
                            title={lang.modeLowDemand}
                        />
                        <RadioButton
                            checked={defaultMode === HIGH_DEMAND_MODE}
                            onChange={() => onChange({ defaultMode: HIGH_DEMAND_MODE })}
                            title={lang.modeHighDemand}
                        />
                        <RadioButton
                            checked={defaultMode === CONTINUOUS_MODE}
                            onChange={() => onChange({ defaultMode: CONTINUOUS_MODE })}
                            title={lang.modeContinuous}
                        />
                    </CheckboxWrapper>
                </FormGroup>

                <FormGroup>
                    <Label>{lang.defaultCalculationMethod}</Label>
                    <CheckboxWrapper displayColumn>
                        <Row>
                            <Col xs={6}>
                                <Label style={{ margin: "0px 0px 8px 15px" }}>{lang.dangerousFailures}</Label>
                                <RadioButton
                                    checked={defaultCalculationMethodDangerous === NO_DANGEROUS_FAILURE}
                                    onChange={() => onChange({ defaultCalculationMethodDangerous: NO_DANGEROUS_FAILURE })}
                                    title={lang.noDangerousFailure}
                                />
                                <RadioButton
                                    checked={defaultCalculationMethodDangerous === IEC_61508_2010}
                                    onChange={() => onChange({
                                        defaultCalculationMethodDangerous: IEC_61508_2010,
                                        defaultCalculationMethodSafe: NO_SAFE_FAILURE,
                                    })}
                                    title={lang.iec_61508_2010}
                                />
                                <RadioButton
                                    checked={defaultCalculationMethodDangerous === IEC_61508_1998}
                                    onChange={() => onChange({
                                        defaultCalculationMethodDangerous: IEC_61508_1998,
                                        defaultCalculationMethodSafe: NO_SAFE_FAILURE,
                                    })}
                                    title={lang.iec_61508_1998}
                                />
                                <RadioButton
                                    checked={defaultCalculationMethodDangerous === VDI_VDE_2180_2019}
                                    onChange={() => onChange({
                                        defaultCalculationMethodDangerous: VDI_VDE_2180_2019,
                                        defaultCalculationMethodSafe: NO_SAFE_FAILURE,
                                    })}
                                    title={lang.vdi_vde_2180_2019}
                                />
                                {
                                    // TODO: temporarily disabled
                                    /*
                                    <RadioButton
                                        checked={defaultCalculationMethodDangerous === ISA_TR84_2002}
                                        onChange={() => onChange({
                                            defaultCalculationMethodDangerous: ISA_TR84_2002,
                                            defaultCalculationMethodSafe: NO_SAFE_FAILURE,
                                        })}
                                        title={lang.isa_tr84_2002}
                                    />
                                    */
                                }
                                <RadioButton
                                    checked={defaultCalculationMethodDangerous === ISA_TR84_2022}
                                    onChange={() => onChange({
                                        defaultCalculationMethodDangerous: ISA_TR84_2022,
                                        defaultCalculationMethodSafe: NO_SAFE_FAILURE,
                                    })}
                                    title={lang.isa_tr84_2022}
                                />
                            </Col>
                            <Col xs={6}>
                                <Label style={{ margin: "0px 0px 8px 15px" }}>{lang.safeFailures}</Label>
                                <RadioButton
                                    checked={defaultCalculationMethodSafe === NO_SAFE_FAILURE}
                                    onChange={() => onChange({ defaultCalculationMethodSafe: NO_SAFE_FAILURE })}
                                    title={lang.noSafeFailure}
                                />
                                <RadioButton
                                    checked={defaultCalculationMethodSafe === ISA_TR84_2022}
                                    onChange={() => onChange({
                                        defaultCalculationMethodSafe: ISA_TR84_2022,
                                        defaultCalculationMethodDangerous: NO_DANGEROUS_FAILURE,
                                    })}
                                    title={lang.isa_tr84_2022}
                                />
                            </Col>
                        </Row>
                    </CheckboxWrapper>
                </FormGroup>

                <FormGroup>
                    <Label>{lang.defaultArchitecturalConstraints}</Label>
                    <CheckboxWrapper displayColumn>
                        <RadioButton
                            checked={defaultArchitecturalConstraints === NO_ARCHITECTURAL_CONSTRAINTS}
                            onChange={() => onChange({ defaultArchitecturalConstraints: NO_ARCHITECTURAL_CONSTRAINTS })}
                            title={lang.noArchitecturalConstraints}
                        />
                        <RadioButton
                            checked={defaultArchitecturalConstraints === IEC_61508_2010}
                            onChange={() => onChange({ defaultArchitecturalConstraints: IEC_61508_2010 })}
                            title={lang.iec_61508_2010}
                        />
                        <RadioButton
                            checked={defaultArchitecturalConstraints === IEC_61508_1998}
                            onChange={() => onChange({ defaultArchitecturalConstraints: IEC_61508_1998 })}
                            title={lang.iec_61508_1998}
                        />
                        <RadioButton
                            checked={defaultArchitecturalConstraints === IEC_61511_2016}
                            onChange={() => onChange({ defaultArchitecturalConstraints: IEC_61511_2016 })}
                            title={lang.iec_61511_2016}
                        />
                        <RadioButton
                            checked={defaultArchitecturalConstraints === IEC_61511_2003}
                            onChange={() => onChange({ defaultArchitecturalConstraints: IEC_61511_2003 })}
                            title={lang.iec_61511_2003}
                        />
                    </CheckboxWrapper>
                </FormGroup>

                { viewAccess && <DefaultAccountSettings /> }
            </Col>
        </Row>
    )
}
export default MainSettings
